import NotificationService from "@/common/notification.service";
import { i18n } from "@/i18n";
import router from "@/router";
import { useAuthStore } from "@/stores";

import { getErrorMessage } from "./utils";

/**
 * @param {AxiosResponse<ApiResponse>} response
 *
 * @returns {boolean}
 */
const isNoAccountResponse = (response) =>
  response?.status === 401 && response?.data?.message === "no_account";

/**
 * @param {AxiosResponse<ApiResponse>} response
 *
 * @returns {boolean}
 */
const isUniloginUnavailableResponse = (response) =>
  response?.status === 503 && response?.data?.message === "unilogin_unavailable";

/**
 * @param {AxiosError<ApiResponse>} error
 *
 * @returns {Promise<AxiosResponse<ApiResponse>>}
 */
export const responseErrorInterceptor = async (error) => {
  const errorStatus = error?.response?.status;
  const errorMessage = getErrorMessage(error);

  if (isUniloginUnavailableResponse(error.response)) {
    await router.push({ name: "unilogin-unavailable" });
    throw error;
  }

  // The user will be rejected with a no account response if none of the institutions they are
  // attached to have an connection to an Ordbogen company. Redirect to a pretty error page.
  if (isNoAccountResponse(error.response)) {
    await router.push({ name: "no-account" });
    throw error;
  }

  // User not authenticated (either token expired or client has no token set)
  if (errorStatus === 401) {
    const authStore = useAuthStore();

    if (authStore.isAuthenticated) {
      NotificationService.error(i18n.global.t("api.auth_failed"));
    } else {
      NotificationService.error(i18n.global.t("api.not_authenticated"));
    }

    // Need to reset frontend state
    authStore.resetAuth();

    await router.push({ name: "login" });
  }

  // User doesn't have the right permissions to view the content
  if (errorStatus === 403) {
    NotificationService.error(
      errorMessage === "operation_not_allowed"
        ? i18n.global.t("api.operation_not_allowed")
        : i18n.global.t("api.access_denied")
    );
  }

  // Content not found
  if (errorStatus === 404) {
    NotificationService.error(
      errorMessage === "invalid_code"
        ? i18n.global.t("api.invalid_code")
        : i18n.global.t("api.not_found")
    );
  }

  // Uploaded file was too big
  if (errorStatus === 413) {
    NotificationService.error(i18n.global.t("api.413"));
  }

  // When the error is a server error, we show a toast.
  if (error.response?.status >= 500) {
    NotificationService.error(i18n.global.t("api.error"));
  }

  throw error;
};
